/**
 * ThumbnailPreview Component
 *
 * Wraps class names to show thumbnail preview on hover.
 * Features:
 * - 300ms delay before showing thumbnail
 * - Portal rendering for proper z-index
 * - Smooth fade-in animation
 * - Positioning near cursor
 */

import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useCategoryContext } from '@/contexts/CategoryContext'
import { useAuthenticatedImage } from '@/hooks/useAuthenticatedImage'

interface ThumbnailPreviewProps {
  className: string
  thumbnailUrl?: string | null
  children: React.ReactNode
  disabled?: boolean
}

// Helper to parse image URL
const parseImageUrl = (imageUrl: string): { classId: string; filename: string } | null => {
  try {
    const url = new URL(imageUrl, window.location.origin)
    const classId = url.searchParams.get('class_id')
    const filename = url.searchParams.get('filename')

    if (!classId || !filename) {
      return null
    }

    return { classId, filename }
  } catch (error) {
    console.error('Failed to parse image URL:', imageUrl, error)
    return null
  }
}

export const ThumbnailPreview: React.FC<ThumbnailPreviewProps> = ({
  className,
  thumbnailUrl,
  children,
  disabled = false,
}) => {
  const { getClassThumbnail } = useCategoryContext()
  const [showPreview, setShowPreview] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const timeoutRef = useRef<number>()
  const elementRef = useRef<HTMLSpanElement>(null)

  // Get thumbnail if not provided
  const thumbnail = thumbnailUrl || getClassThumbnail(className)?.url
  const enabled = !disabled && !!thumbnail

  // Parse the thumbnail URL to get classId and filename
  const parsedUrl = thumbnail ? parseImageUrl(thumbnail) : null

  // Use the authenticated image hook only when preview is shown
  // Use 'small' size (300px) for thumbnail previews
  const { blobUrl, loading, error } = useAuthenticatedImage(
    showPreview && parsedUrl ? parsedUrl.classId : '',
    showPreview && parsedUrl ? parsedUrl.filename : '',
    'small'
  )

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!enabled) return
    // Set initial position
    updatePosition(e)

    // Show preview after 300ms delay
    timeoutRef.current = window.setTimeout(() => {
      setShowPreview(true)
    }, 300)
  }

  const handleMouseLeave = () => {
    if (!enabled) return
    // Cancel pending preview
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
    }
    setShowPreview(false)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!enabled) return
    updatePosition(e)
  }

  const updatePosition = (e: React.MouseEvent) => {
    // Position preview 20px to the right and 20px below cursor
    const offsetX = 20
    const offsetY = 20

    // Calculate position
    let x = e.clientX + offsetX
    let y = e.clientY + offsetY

    // Prevent preview from going off-screen
    const previewWidth = 200 // max-w-[200px]
    const previewHeight = 200 // max-h-[200px]
    const padding = 10

    // Check right edge
    if (x + previewWidth + padding > window.innerWidth) {
      x = e.clientX - previewWidth - offsetX
    }

    // Check bottom edge
    if (y + previewHeight + padding > window.innerHeight) {
      y = e.clientY - previewHeight - offsetY
    }

    setPosition({ x, y })
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Hide preview if there's an error
  useEffect(() => {
    if (error) {
      console.error(`Failed to load thumbnail for ${className}:`, error)
      setShowPreview(false)
    }
  }, [error, className])

  const preview = enabled && showPreview && (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <div
        className={`
          bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-2
          transition-opacity duration-200
          ${!loading && blobUrl ? 'opacity-100' : 'opacity-0'}
        `}
      >
        <div className="w-[200px] h-[200px] flex items-center justify-center bg-gray-50 rounded">
          {loading ? (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          ) : blobUrl ? (
            <img
              src={blobUrl}
              alt={`${className} thumbnail`}
              className="max-w-[200px] max-h-[200px] object-contain rounded"
            />
          ) : null}
        </div>
        <div className="mt-1 text-xs text-gray-600 text-center truncate max-w-[200px]">
          {className}
        </div>
      </div>
    </div>
  )

  return (
    <>
      <span
        ref={elementRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        className="cursor-help"
      >
        {children}
      </span>
      {enabled && showPreview && createPortal(preview, document.body)}
    </>
  )
}

export default ThumbnailPreview
